<template>
  <section class="py-9 tablet:py-20">
    <div class="container-wrap">
      <div class="desktop:w-2/3">
        <h2 class="text-h2 mb-10 tablet:line-clamp-2">
          {{ title }}
        </h2>
        <TheRichText
          v-if="descriptionHtml || description"
          class="mb-6 w-full text-sm leading-6 text-gray-700 tablet:mb-10 tablet:line-clamp-7 tablet:text-base"
          :text="descriptionHtml || description"
          :is-html="!!descriptionHtml"
        />
      </div>

      <ul class="flex flex-col gap-4 desktop:h-[700px] desktop:flex-row">
        <li
          v-for="(item, index) in items"
          :key="item.name"
          class="flex flex-col overflow-hidden rounded-3xl"
          :class="[
            BG_CLASSES[index % BG_CLASSES.length],
            index === activeIndex
              ? 'desktop:min-w-0 desktop:flex-1'
              : 'cursor-pointer desktop:w-[200px] desktop:shrink-0',
          ]"
        >
          <div
            class="flex flex-col gap-3 p-3 tablet:gap-4 tablet:p-4 desktop:flex-1"
            @click="handleClick(index)"
          >
            <div
              class="grid grid-cols-[auto_1fr_auto] items-center gap-x-3 tablet:gap-x-4 desktop:gap-x-5 desktop:gap-y-5"
            >
              <div
                class="col-start-1 row-start-1 text-5xl leading-[52px] text-brand-400"
                style="font-weight: 700"
              >
                {{ String(index + 1).padStart(2, '0') }}
              </div>

              <div
                v-if="index === activeIndex && items.length > 1"
                class="col-start-2 row-start-1 hidden justify-self-start rounded-full bg-gray-600_a15 desktop:flex"
              >
                <button
                  class="rounded-full p-3 hover:bg-gray-600_a25 active:bg-gray-600_a35"
                  @click.stop="handleStep(-1)"
                >
                  <IconCaret direction="left" class="h-7 w-7" />
                </button>
                <button
                  class="rounded-full p-3 hover:bg-gray-600_a25 active:bg-gray-600_a35"
                  @click.stop="handleStep(1)"
                >
                  <IconCaret direction="right" class="h-7 w-7" />
                </button>
              </div>

              <h3
                class="col-start-2 row-start-1 min-w-0 text-brand-400 desktop:col-span-3 desktop:col-start-1 desktop:row-start-2"
                :class="
                  index === activeIndex
                    ? 'text-h4'
                    : 'text-h4 desktop:text-web-h5'
                "
              >
                {{ item.name }}
              </h3>

              <span
                class="col-start-3 row-start-1 rounded-full bg-gray-600_a15 p-3 desktop:hidden"
              >
                <IconCaret
                  :direction="index === activeIndex ? 'up' : 'down'"
                  class="h-6 w-6 tablet:h-7 tablet:w-7"
                />
              </span>
            </div>

            <p
              class="leading-6 text-gray-700 desktop:flex desktop:flex-1 desktop:flex-col"
              :class="
                index === activeIndex
                  ? 'text-base'
                  : 'text-base desktop:justify-end desktop:text-sm'
              "
            >
              {{ item.description }}
            </p>
          </div>

          <ul
            v-if="index === activeIndex"
            class="flex flex-col gap-3 py-3 tablet:gap-4 tablet:py-4 desktop:pt-0"
          >
            <li v-for="subItem in item.items" :key="subItem.name">
              <TheNuxtLink
                :to="subItem.link"
                :title="subItem.name"
                class="group grid grid-cols-1 overflow-hidden bg-white shadow-card hover:shadow-blue-400_a3 tablet:grid-cols-5"
              >
                <div class="flex flex-col p-3 tablet:col-span-3 tablet:p-4">
                  <h4 class="text-h4 mb-2 truncate tablet:mb-3">
                    {{ subItem.name }}
                  </h4>
                  <ul class="flex flex-1 flex-col justify-end gap-1">
                    <li
                      v-for="feature in subItem.features"
                      :key="feature"
                      class="flex items-start gap-3"
                    >
                      <IconSuccess class="h-6 w-6 shrink-0 text-green-400" />
                      <span class="text-sm leading-6 tablet:text-base">
                        {{ feature }}
                      </span>
                    </li>
                  </ul>
                </div>
                <figure
                  class="relative aspect-video overflow-hidden tablet:col-span-2 tablet:aspect-[4/3] desktop:aspect-[2/1]"
                >
                  <img
                    :src="subItem.thumbnail"
                    :alt="subItem.name"
                    loading="lazy"
                    class="pointer-events-none absolute left-0 top-0 block h-full w-full object-cover"
                  />
                  <TheMediaButton
                    class="absolute bottom-3 right-3 tablet:bottom-4 tablet:right-4 desktop:bottom-5 desktop:right-5"
                    :theme="theme"
                    :text="$t('card.item-button')"
                    :has-shadow="true"
                  />
                </figure>
              </TheNuxtLink>
            </li>
          </ul>
        </li>
      </ul>
    </div>
  </section>
</template>

<script setup lang="ts">
  /**
   * @see https://www.figma.com/design/QJ3ZvEbUHI9xI11iudYf8U/Advantech-WISE-Library?node-id=12796-23843&t=MHxdWVLPYfdtg73l-0
   */

  interface ISubItem {
    name: string;
    link: string;
    thumbnail: string;
    features: string[];
  }

  interface IItem {
    name: string;
    description: string;
    items: ISubItem[];
  }

  interface IProps {
    title: string;
    description?: string;
    descriptionHtml?: string;
    items: IItem[];
    theme?: TTheme;
  }

  const props = withDefaults(defineProps<IProps>(), {
    description: undefined,
    descriptionHtml: undefined,
    theme: 'blue',
  });

  const BG_CLASSES = ['bg-brand-400_a1', 'bg-brand-400_a2', 'bg-brand-400_a3'];

  const { isDesktop } = useScreen();

  const activeIndex = ref<number>(0);

  const handleClick = (index: number): void => {
    if (index !== activeIndex.value) {
      activeIndex.value = index;
    } else if (!isDesktop.value) {
      activeIndex.value = -1;
    }
  };

  const handleStep = (step: number): void => {
    const total = props.items.length;
    activeIndex.value = (activeIndex.value + step + total) % total;
  };

  // 桌機版必須有一個展開的項目
  watch(isDesktop, (value) => {
    if (value && activeIndex.value < 0) activeIndex.value = 0;
  });

  export type { IItem, ISubItem };
</script>
